import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PayerDetails } from '@/services/scheduling/use-prebook-selected-patient';

/**
 * GET /api/Billing/Agreements/CorporatePayerDetails
 *   ?organizationId=&showArchived=&q=
 *
 * Mirrors Flutter `searchCorporate()` in
 * `common_data_source_impl.dart:828-835` — typeahead for the corporate
 * sponsor field in the Corporate Agreements editor. Disabled until the
 * user types at least 2 chars (matches the Flutter dropdown behaviour).
 *
 * Same shape as `useSearchInsurancePayers` but a different endpoint and
 * data — keeping them separate avoids tangling the cache keys.
 */

interface UseSearchCorporateSponsorsParams {
  organizationId: string;
  query: string;
  showArchived?: boolean;
}

export function useSearchCorporateSponsors({
  organizationId,
  query,
  showArchived = false,
}: UseSearchCorporateSponsorsParams) {
  const trimmed = query.trim();
  const enabled = !!organizationId && trimmed.length >= 2;

  return useQuery<PayerDetails[]>({
    queryKey: ['corporate-sponsors', { organizationId, query: trimmed, showArchived }],
    enabled,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        organizationId,
        showArchived: String(showArchived),
        q: trimmed,
      });
      // Same 204-No-Content quirk as the insurance endpoint — coerce to
      // [] so the dropdown shows "No matches" instead of tripping the
      // global error toast.
      const result = await get<PayerDetails[] | undefined>(
        `/api/Billing/Agreements/CorporatePayerDetails?${params.toString()}`,
        { signal },
      );
      return result ?? [];
    },
  });
}
